import { GateLiveStatusOutcome } from '@/__generated__/graphql';
import { AU_BTN_SOLID_ERROR_SX, auToneSurfaceSx, type AuTone } from '@/theme/au-compat';
import { useStopGateOperation } from '@features/gateCommon/hooks/useStopGateOperation';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Button, Typography } from '@mui/material';
import { type FC, type ReactNode } from 'react';
import type { GateLiveStatusState } from '../hooks/useGateLiveStatus';

const Notice: FC<{
  tone: AuTone;
  children: ReactNode;
  detail?: string | null;
  action?: ReactNode;
}> = ({ tone, children, detail, action }) => (
  <Box
    role="status"
    sx={{ ...auToneSurfaceSx(tone), display: 'flex', alignItems: 'center', gap: '12px' }}
  >
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 13, color: 'inherit' }}>{children}</Typography>
      {/* El motivo que armó el backend: distingue, p. ej., una compuerta no habilitada de una
          respuesta inválida, que el texto de arriba agrupa. */}
      {detail && (
        <Typography sx={{ fontSize: 12, color: 'inherit', opacity: 0.8 }}>{detail}</Typography>
      )}
    </Box>
    {action}
  </Box>
);

/** Lo que el operario tiene que saber antes de pedir un movimiento; nada si todo está en orden. */
export const LiveNotice: FC<{ gateId: string | null; live: GateLiveStatusState }> = ({
  gateId,
  live,
}) => {
  const { i18n } = useLingui();
  const { stopOperation, loading: stopping } = useStopGateOperation();
  const { liveStatus, awaiting, failed, deviceSilent, reading, refresh, lastKnownMoving } = live;
  if (!gateId) return null;

  // Una operación que la base todavía no ve (manual en terreno, o el `g/o` aún no llegó): no se
  // pide otra encima, y como el widget tampoco la ve, detenerla sólo es posible desde acá. Se
  // mantiene mientras se refresca o si el refresco falla: vale la última lectura.
  if (lastKnownMoving)
    return (
      <Notice
        tone="warning"
        action={
          <Button
            onClick={async () => {
              await stopOperation(gateId);
              refresh();
            }}
            disabled={stopping}
            sx={{ ...AU_BTN_SOLID_ERROR_SX, flexShrink: 0 }}
          >
            <Trans>Detener</Trans>
          </Button>
        }
      >
        {reading?.moving ? (
          <Trans>
            La compuerta está en movimiento. Detenla o espera a que termine para pedir otro
            movimiento.
          </Trans>
        ) : (
          <Trans>
            Según la última lectura del equipo, la compuerta está en movimiento. Detenla o espera a
            que termine para pedir otro movimiento.
          </Trans>
        )}
      </Notice>
    );
  if (awaiting) return null;

  if (deviceSilent)
    return (
      <Notice tone="warning">
        <Trans>
          Equipo desconectado: la solicitud quedará en cola hasta que se conecte. Se usan la última
          posición y los límites registrados.
        </Trans>
      </Notice>
    );
  if (failed || !liveStatus || liveStatus.outcome === GateLiveStatusOutcome.Unavailable)
    return (
      <Notice tone="info" detail={liveStatus?.message}>
        <Trans>
          Estado en vivo no disponible. Se usan la última posición y los límites registrados.
        </Trans>
      </Notice>
    );
  if (liveStatus.outcome === GateLiveStatusOutcome.Rejected) {
    const code = liveStatus.code ?? '—';
    return (
      <Notice tone="warning" detail={liveStatus.message}>
        {t(
          i18n,
        )`El equipo rechazó la consulta de estado (código ${code}). Se usan la última posición y los límites registrados.`}
      </Notice>
    );
  }
  return null;
};
